import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CategoryAllocationSliders = ({ categories, totalBudget, onCategoryUpdate }) => {
  const [localCategories, setLocalCategories] = useState(categories);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setLocalCategories(categories);
  }, [categories]);

  const handleSliderChange = (categoryId, newPercentage) => {
    const updatedCategories = localCategories?.map(cat => {
      if (cat?.id === categoryId) {
        const allocated = (totalBudget * newPercentage) / 100;
        return { ...cat, percentage: newPercentage, allocated };
      }
      return cat;
    });

    setLocalCategories(updatedCategories);
    setHasChanges(true);
  };

  const handleSave = () => {
    onCategoryUpdate(localCategories);
    setHasChanges(false);
  };

  const handleReset = () => {
    setLocalCategories(categories);
    setHasChanges(false);
  };

  const totalPercentage = localCategories?.reduce((sum, cat) => sum + cat?.percentage, 0);
  const isValid = totalPercentage === 100;

  const getStatusColor = (spent, allocated) => {
    const percentage = (spent / allocated) * 100;
    if (percentage >= 90) return 'var(--color-error)';
    if (percentage >= 75) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  return (
    <div className="bg-card rounded-2xl p-4 md:p-6 shadow-elevation-2">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
            <Icon name="SlidersHorizontal" size={20} color="var(--color-secondary)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">Category Allocation</h2>
            <p className="text-sm text-muted-foreground">Customize budget distribution across expense categories</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
            isValid ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
          }`}>
            {totalPercentage}% {isValid ? '✓' : '⚠'}
          </div>
        </div>
      </div>

      {!isValid && (
        <div className="mb-4 p-3 bg-warning/10 border border-warning/20 rounded-lg flex items-start gap-2">
          <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />
          <p className="text-sm text-warning flex-1">
            Total allocation must equal 100%. Current total: {totalPercentage}%
          </p>
        </div>
      )}

      <div className="space-y-6">
        {localCategories?.map((category) => {
          const spentPercentage = (category?.spent / category?.allocated) * 100;
          const statusColor = getStatusColor(category?.spent, category?.allocated);

          return (
            <div key={category?.id} className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center" 
                    style={{ backgroundColor: `${category?.color}15` }}
                  >
                    <Icon name={category?.icon} size={20} color={category?.color} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{category?.name}</p>
                    <p className="text-xs text-muted-foreground">
                      ${category?.spent?.toLocaleString()} / ${category?.allocated?.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold" style={{ color: category?.color }}>
                    {category?.percentage}%
                  </p>
                  <p className="text-xs text-muted-foreground">
                    ${category?.allocated?.toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={category?.percentage}
                  onChange={(e) => handleSliderChange(category?.id, parseInt(e?.target?.value))}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, ${category?.color} 0%, ${category?.color} ${category?.percentage}%, var(--color-muted) ${category?.percentage}%, var(--color-muted) 100%)`
                  }}
                />
                
                <div className="relative w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="absolute top-0 left-0 h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.min(spentPercentage, 100)}%`,
                      backgroundColor: statusColor
                    }}
                  />
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span style={{ color: statusColor }}>
                    {spentPercentage?.toFixed(0)}% spent
                  </span>
                  <span className="text-muted-foreground">
                    ${(category?.allocated - category?.spent)?.toLocaleString()} remaining
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {hasChanges && (
        <div className="flex gap-3 mt-6 pt-4 border-t border-border">
          <Button 
            variant="default" 
            fullWidth 
            iconName="Save" 
            iconPosition="left"
            onClick={handleSave}
            disabled={!isValid}
          >
            Save Changes
          </Button>
          <Button 
            variant="outline" 
            fullWidth 
            onClick={handleReset}
          >
            Reset
          </Button>
        </div>
      )}
    </div>
  );
};

export default CategoryAllocationSliders;